import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import PublicNavbar from '@/components/layout/PublicNavbar';
import PublicFooter from '@/components/layout/PublicFooter';
import { ArrowRight, Search, Sparkles, ShieldCheck, CheckCircle2, FlaskConical, TestTube, FileText, Droplet, Wind, Sun, Package, Home } from 'lucide-react';

const FORMULATION_DATA = [
  {
    id: "extrait",
    title: "Extrait de Parfum",
    shortDesc: "Konsentrasi tertinggi (20-30%)",
    content: {
      intro: "Diambil dari bahasa Prancis, Extrait de Parfum berarti Perfume Extract atau Pure Perfume. Ini adalah jenis parfum dengan konsentrasi minyak wangi paling tinggi, berkisar antara 20–40%. Karena konsentrasinya tinggi, wanginya cenderung sangat kuat, kompleks, dan tahan lama (bahkan bisa lebih dari 24 jam).",
      usageTitle: "Jenis Kegiatan yang Cocok:",
      usages: [
        { title: "Acara Formal dan Mewah", desc: "Dinner romantis, fine dining, atau kondangan eksklusif. Wanginya elegan dan meninggalkan kesan berkelas." },
        { title: "Meeting dan Event Penting", desc: "Presentasi bisnis atau pertemuan eksekutif. Wanginya bikin Anda terlihat profesional dan high value." },
        { title: "Momen Spesial & Personal Branding", desc: "Kencan pertama atau reuni. Aroma yang tahan lama bikin kehadiran Anda memorable." }
      ]
    }
  },
  {
    id: "edp",
    title: "Eau de Parfum (EDP)",
    shortDesc: "Konsentrasi ideal (15-20%)",
    content: {
      intro: "Eau de Parfum memiliki konsentrasi minyak wangi sedikit lebih rendah dari Extrait, yakni sekitar 15-20%. Wanginya masih sangat tercium jelas, berkarakter, dan biasanya mampu bertahan sekitar 6-8 jam di kulit (atau lebih lama di pakaian).",
      usageTitle: "Jenis Kegiatan yang Cocok:",
      usages: [
        { title: "Aktivitas Harian Profesional", desc: "Kerja di ruangan ber-AC, meeting rutin, atau ngantor seharian tanpa perlu sering semprot ulang." },
        { title: "Hangout Berkualitas", desc: "Nongkrong santai di cafe atau jalan-jalan di mall. Memberikan aura positif yang tahan lama." },
        { title: "Acara Semi-Formal", desc: "Undangan pernikahan atau pesta ulang tahun. Pas untuk tampil stand out tanpa terlalu mencolok." }
      ]
    }
  },
  {
    id: "edt",
    title: "Eau de Toilette (EDT)",
    shortDesc: "Menyegarkan & Ringan (5-15%)",
    content: {
      intro: "Eau de Toilette adalah jenis wewangian dengan konsentrasi 5-15%. Karakternya jauh lebih ringan, segar, dan tidak menusuk, menjadikannya pilihan paling aman untuk cuaca tropis yang panas. Biasanya bertahan 3-5 jam.",
      usageTitle: "Jenis Kegiatan yang Cocok:",
      usages: [
        { title: "Aktivitas Outdoor & Cuaca Panas", desc: "Jalan-jalan siang hari, liburan ke pantai, atau aktivitas luar ruangan. Kesegarannya tidak bikin eneg saat berkeringat." },
        { title: "Olahraga Ringan", desc: "Nge-gym santai atau jogging pagi. Memberikan kesan fresh dan clean." },
        { title: "Kasual Harian", desc: "Ke kampus, belanja ke supermarket, atau bersantai di rumah." }
      ]
    }
  },
  {
    id: "mist",
    title: "Hair & Body Mist",
    shortDesc: "Kesegaran Instan (<5%)",
    content: {
      intro: "Body Mist atau Hair Mist memiliki konsentrasi paling rendah (di bawah 5%) dengan kandungan air/alkohol yang lebih banyak. Didesain untuk memberikan kesegaran instan yang aman disemprotkan langsung ke seluruh tubuh bahkan rambut.",
      usageTitle: "Jenis Kegiatan yang Cocok:",
      usages: [
        { title: "Setelah Mandi (After Shower)", desc: "Menjaga aroma sabun tetap segar dan memberikan efek relaksasi." },
        { title: "Touch-up Kilat", desc: "Disemprotkan berkali-kali kapan saja saat butuh kesegaran ekstra tanpa takut wanginya berlebihan." },
        { title: "Aktivitas Santai", desc: "Teman tidur, bersantai di kamar, atau sekadar penyegar instan di cuaca terik." }
      ]
    }
  },
  {
    id: "discovery",
    title: "Discovery Set (Tester)",
    shortDesc: "Strategi Sampling (Vial)",
    content: {
      intro: "Discovery Set bukanlah sebuah konsentrasi parfum, melainkan strategi pengemasan produk dalam bentuk vial atau botol mini (biasanya 2ml - 5ml). Ini adalah langkah cerdas bagi brand parfum untuk memperkenalkan seluruh koleksi aroma mereka kepada calon pelanggan tanpa mengharuskan pelanggan membeli ukuran full-size terlebih dahulu.",
      usageTitle: "Keuntungan Membuat Discovery Set:",
      usages: [
        { title: "Meningkatkan Konversi Penjualan", desc: "Pelanggan lebih mudah mengambil keputusan pembelian untuk ukuran full-size setelah mencoba dan menemukan aroma favorit mereka dari Discovery Set." },
        { title: "Strategi Marketing yang Efektif", desc: "Sangat cocok untuk dijadikan PR Package kepada influencer atau bundel hadiah promosi karena bentuknya yang compact, elegan, dan estetik." },
        { title: "Menjangkau Pasar Lebih Luas", desc: "Harganya yang jauh lebih terjangkau memungkinkan brand Anda menjangkau audiens dan demografi pasar yang jauh lebih luas." }
      ]
    }
  },
  {
    id: "reed",
    title: "Reed Diffuser",
    shortDesc: "Pengharum Ruangan",
    content: {
      intro: "Berbeda dengan parfum badan, Reed Diffuser diformulasikan khusus sebagai aromaterapi ruangan. Menggunakan batang rotan (reed) untuk menyerap minyak esensial dan menyebarkan aromanya secara konstan tanpa perlu listrik atau api.",
      usageTitle: "Area Penempatan Terbaik:",
      usages: [
        { title: "Kamar Tidur", desc: "Gunakan aroma calming seperti lavender atau chamomile untuk membantu relaksasi dan tidur nyenyak." },
        { title: "Ruang Tamu & Keluarga", desc: "Aroma floral atau woody untuk menyambut tamu dengan suasana mewah dan hangat." },
        { title: "Kamar Mandi / Toilet", desc: "Aroma sitrus atau mint untuk menghilangkan bau tidak sedap dan menjaga kesegaran ruangan." }
      ]
    }
  }
];

export default function FormulasiPage() {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState("extrait");

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <>
      <div className="min-h-screen bg-[#FFFDF9] text-[#4A3B32] font-sans selection:bg-[#D4AF37] selection:text-white">
        <PublicNavbar />

        {/* Hero Section */}
        <section className="relative pt-24 pb-32 px-6 lg:px-12 bg-gradient-to-br from-[#FFFDF9] to-[#F9F5EC] overflow-hidden" data-aos="fade-up">
          <div className="absolute right-0 top-0 bottom-0 w-full lg:w-1/2 opacity-20 lg:opacity-100 z-0 hidden md:block">
            <img src="https://images.unsplash.com/photo-1576426863848-c21f53c60b19?q=80&w=1200&auto=format&fit=crop" alt="Lab Formulasi" className="w-full h-full object-cover object-left mask-image-gradient-left" />
            <div className="absolute inset-0 bg-gradient-to-r from-[#F9F5EC] via-[#F9F5EC]/80 to-transparent"></div>
          </div>

          <div className="container mx-auto max-w-7xl relative z-10">
            <div className="w-full lg:w-1/2 pt-10">
              <span className="inline-block py-1.5 px-4 bg-[#E6D5B8]/40 text-[#8C6208] text-[10px] font-bold tracking-widest uppercase rounded mb-4">
                LAYANAN FORMULASI
              </span>
              <h1 className="text-4xl md:text-5xl lg:text-5xl font-bold tracking-tight mb-6 leading-[1.15] text-[#302621]">
                Formulasi Inovatif untuk <br /> Produk Kosmetik yang <br /> Efektif & Aman
              </h1>
              <p className="text-base text-[#7A695B] mb-10 leading-relaxed max-w-md font-light">
                Kami menghadirkan formulasi inovatif dengan bahan aktif terbaik untuk menghasilkan produk kosmetik yang efektif, aman, dan sesuai regulasi.
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <Button size="lg" className="bg-[#D4AF37] hover:bg-[#B58B22] text-white px-8 py-6 text-base rounded-lg shadow-lg shadow-[#D4AF37]/20 border-0 transition-transform hover:-translate-y-1">
                  Konsultasi Formulasi <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
                <Button size="lg" variant="outline" className="border-[#302621] text-[#302621] bg-transparent hover:bg-[#302621] hover:text-white px-8 py-6 text-base rounded-lg transition-transform hover:-translate-y-1">
                  Lihat Proses
                </Button>
              </div>
            </div>
          </div>
        </section>

        {/* 4 Poin Kunci */}
        <section className="relative -mt-16 px-6 lg:px-12 z-20" data-aos="fade-up">
          <div className="container mx-auto max-w-6xl">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6">
              {[
                { title: "Riset & Inovasi", desc: "Formulasi terkini sesuai trend pasar.", icon: <Search /> },
                { title: "Bahan Aktif Premium", desc: "Menggunakan bahan baku berkualitas tinggi.", icon: <Sparkles /> },
                { title: "Aman & Teruji", desc: "Melewati uji stabilitas dan keamanan produk.", icon: <ShieldCheck /> },
                { title: "Sesuai Regulasi", desc: "Memenuhi standar BPOM dan Halal.", icon: <CheckCircle2 /> }
              ].map((feat, idx) => (
                <div key={idx} className="bg-white p-6 md:p-8 rounded-2xl shadow-xl shadow-[#302621]/5 border border-[#E6D5B8]/30 flex flex-col hover:border-[#D4AF37] hover:shadow-md transition-all group">
                  <div className="w-12 h-12 rounded-xl bg-[#F9F5EC] flex items-center justify-center text-[#D4AF37] mb-6 group-hover:scale-110 transition-transform">
                    {feat.icon}
                  </div>
                  <h4 className="font-bold text-[#302621] mb-2 text-sm">{feat.title}</h4>
                  <p className="text-xs text-[#7A695B] leading-relaxed">{feat.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Keunggulan Layanan Formulasi Kami */}
        <section className="py-24 px-6 lg:px-12 bg-white" data-aos="fade-up">
          <div className="container mx-auto max-w-6xl">
            <div className="flex flex-col lg:flex-row gap-16 items-center">
              <div className="w-full lg:w-5/12">
                <div className="relative rounded-2xl overflow-hidden shadow-lg border border-[#E6D5B8]/40 aspect-[4/5]">
                  <img src="https://images.unsplash.com/photo-1556228578-0d85b1a4d571?q=80&w=800&auto=format&fit=crop" alt="Lab Scientist" className="w-full h-full object-cover" />
                </div>
              </div>
              <div className="w-full lg:w-7/12">
                <h2 className="text-3xl font-bold text-[#302621] mb-4 text-center md:text-left">Keunggulan Layanan Formulasi Kami</h2>
                <p className="text-[#7A695B] mb-10 text-center md:text-left">Formulasi yang kami kembangkan dirancang untuk memberikan hasil terbaik.</p>
                
                <div className="space-y-6">
                  {[
                    { title: "Custom Formula", desc: "Dikembangkan khusus sesuai konsep dan kebutuhan brand Anda.", icon: <FlaskConical /> },
                    { title: "Bahan Aktif Terbaik", desc: "Pemilihan bahan aktif premium untuk hasil maksimal.", icon: <Sparkles /> },
                    { title: "Uji Stabilitas", desc: "Setiap formula diuji stabilitasnya untuk memastikan kualitas.", icon: <TestTube /> },
                    { title: "R&D Internal", desc: "Dikembangkan oleh tim R&D berpengalaman dan profesional.", icon: <Search /> },
                    { title: "Dokumentasi Lengkap", desc: "Dilengkapi dokumen teknis untuk proses registrasi.", icon: <FileText /> }
                  ].map((feat, idx) => (
                    <div key={idx} className="flex gap-4 items-start">
                      <div className="w-8 h-8 shrink-0 rounded-full bg-[#F9F5EC] border border-[#E6D5B8] flex items-center justify-center text-[#D4AF37]">
                        {feat.icon}
                      </div>
                      <div>
                        <h4 className="font-bold text-[#302621] text-sm mb-1">{feat.title}</h4>
                        <p className="text-xs text-[#7A695B] leading-relaxed">{feat.desc}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Proses Pengembangan Formulasi */}
        <section className="py-24 px-6 lg:px-12 bg-[#FCFAF6] border-y border-[#E6D5B8]/30" data-aos="fade-up">
          <div className="container mx-auto max-w-6xl text-center">
            <h2 className="text-3xl font-bold text-[#302621] mb-4">Proses Pengembangan Formulasi</h2>
            <p className="text-[#7A695B] mb-16 max-w-2xl mx-auto">Tahapan terstruktur untuk memastikan formula terbaik untuk produk Anda.</p>
            
            <div className="grid grid-cols-2 md:grid-cols-3 gap-8 relative max-w-4xl mx-auto">
              {[
                { no: "01", title: "Konsultasi Konsep Aroma", desc: "Diskusi profil wangi dan identitas brand" },
                { no: "02", title: "Riset & Analisis Bahan", desc: "Pemilihan bahan aktif dan esensial premium" },
                { no: "03", title: "Pembuatan Sampel Lab", desc: "Proses peracikan dan uji coba skala lab" },
                { no: "04", title: "Uji Stabilitas Formula", desc: "Memastikan konsistensi wangi dan keamanan" },
                { no: "05", title: "Evaluasi & Revisi", desc: "Penyesuaian formula berdasarkan feedback Anda" },
                { no: "06", title: "Finalisasi (Lock Formula)", desc: "Formula akhir dikunci siap untuk mass-pro" }
              ].map((step, idx) => (
                <div key={idx} className="relative z-10 flex flex-col items-center w-full mb-4">
                  <div className="w-12 h-12 rounded-full bg-white border-2 border-[#D4AF37] shadow-sm flex items-center justify-center text-[#8C6208] font-bold text-sm mb-4">
                    {step.no}
                  </div>
                  <h4 className="font-bold text-[#302621] text-sm mb-2 text-center">{step.title}</h4>
                  <p className="text-[11px] text-[#7A695B] text-center leading-relaxed px-2">{step.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Panduan Jenis Formulasi (Tabs) */}
        <section className="py-24 px-6 lg:px-12 bg-white" data-aos="fade-up">
          <div className="container mx-auto max-w-6xl text-center">
            <h2 className="text-3xl font-bold text-[#302621] mb-4">Panduan Jenis Formulasi</h2>
            <p className="text-[#7A695B] mb-12 max-w-2xl mx-auto">Kenali berbagai karakteristik dan konsentrasi wewangian yang kami kembangkan untuk menentukan jenis terbaik bagi brand Anda.</p>

            <div className="flex flex-col lg:flex-row gap-8 max-w-6xl mx-auto text-left">
              {/* Tabs List */}
              <div className="w-full lg:w-1/3 flex flex-col gap-3">
                {FORMULATION_DATA.map((tab, idx) => (
                  <button
                    key={idx}
                    onClick={() => setActiveTab(tab.id)}
                    className={`p-4 rounded-2xl border text-left transition-all duration-300 flex flex-col ${
                      activeTab === tab.id 
                        ? 'bg-[#302621] border-[#302621] shadow-lg scale-105' 
                        : 'bg-[#FCFAF6] border-[#E6D5B8]/40 hover:border-[#D4AF37] hover:bg-white'
                    }`}
                  >
                    <span className={`font-bold text-lg ${activeTab === tab.id ? 'text-[#D4AF37]' : 'text-[#302621]'}`}>
                      {tab.title}
                    </span>
                    <span className={`text-xs mt-1 ${activeTab === tab.id ? 'text-gray-300' : 'text-[#7A695B]'}`}>
                      {tab.shortDesc}
                    </span>
                  </button>
                ))}
              </div>
              
              {/* Tab Content */}
              <div className="w-full lg:w-2/3 bg-white p-6 md:p-10 rounded-3xl border border-[#E6D5B8]/40 shadow-xl shadow-[#302621]/5 min-h-[400px]">
                 {/* Content mapped from activeTab */}
                 {(() => {
                   const activeData = FORMULATION_DATA.find(t => t.id === activeTab);
                   return (
                     <div className="animate-in fade-in slide-in-from-bottom-4 duration-500" key={activeTab}>
                       <h3 className="text-2xl md:text-3xl font-bold text-[#302621] mb-4">Apa Itu {activeData.title}?</h3>
                       <p className="text-[#7A695B] leading-relaxed mb-8">{activeData.content.intro}</p>
                       
                       <h4 className="text-xl font-bold text-[#302621] mb-6">{activeData.content.usageTitle}</h4>
                       <div className="space-y-6">
                         {activeData.content.usages.map((usage, uIdx) => (
                           <div key={uIdx} className="flex gap-4 items-start">
                             <div className="mt-1.5 shrink-0 w-2 h-2 rounded-full bg-[#D4AF37]"></div>
                             <div>
                               <h5 className="font-bold text-[#302621] text-md">{uIdx + 1}. {usage.title}</h5>
                               <p className="text-sm text-[#7A695B] mt-1 leading-relaxed">{usage.desc}</p>
                             </div>
                           </div>
                         ))}
                       </div>
                     </div>
                   );
                 })()}
              </div>
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="py-20 px-6 lg:px-12 bg-white border-t border-[#E6D5B8]/30" data-aos="fade-up">
          <div className="container mx-auto max-w-6xl">
            <div className="bg-[#EFE8D8] rounded-3xl overflow-hidden relative shadow-sm border border-[#E6D5B8]/50">
              <div className="absolute right-0 top-0 bottom-0 w-1/2 opacity-100 mix-blend-multiply hidden md:block">
                <img src="/Gambar Web/Botol berdiri.png" alt="Lab Formulasi CTA" className="w-full h-full object-cover mask-image-gradient-left" />
              </div>
              <div className="relative z-10 p-12 md:p-16 w-full md:w-[55%] lg:w-1/2">
                <h2 className="text-3xl md:text-4xl font-extrabold text-[#302621] mb-4 leading-tight">Punya Ide Produk? Kami Wujudkan!</h2>
                <p className="text-[#7A695B] text-lg leading-relaxed mb-8">
                  Tim formulator kami siap membantu Anda mengembangkan formula terbaik untuk brand Anda.
                </p>
                <Button size="lg" className="bg-[#D4AF37] hover:bg-[#B58B22] text-white px-8 py-4 text-base rounded-lg shadow-md border-0 transition-transform hover:-translate-y-1 font-semibold flex items-center w-fit">
                  Konsultasi Sekarang <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </div>
            </div>
          </div>
        </section>

        {/* Footer */}
        <PublicFooter />
      </div>
    </>
  );
}
